import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaChevronRight } from "react-icons/fa";
import axios from "../../../api/axios";
import HeaderWithBack from "../../../components/HeaderWithBack";
import { useAuthStore } from "../../../stores/authStores";
import { useCheckoutStore } from "../../../stores/checkoutStore";
import { useNavigate } from "react-router-dom";

interface PaymentMethodData {
  id: number;
  name: string;
  code: string;
  image_url?: string;
  displayName?: string;
  displayIcon?: string;
}

interface Props {
  totalPrice: number;
  defaultPayment?: PaymentMethodData | null;
  onSelect: (payment: PaymentMethodData, finalTotal: number) => void;
}

const PaymentMethod: React.FC<Props> = ({ totalPrice, defaultPayment, onSelect }) => {
    const token = useAuthStore((state) => state.token);
    const navigate = useNavigate();
    const [step, setStep] = useState<"menu" | "detail">("menu");
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethodData[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const selectedPayment = useCheckoutStore((s) => s.selectedPayment);
    const setPayment = useCheckoutStore((s) => s.setPayment);

    useEffect(() => {
        if (!token) return;

    axios
        .get(`payment?configuration=category_payment_live_consultation`, {
            headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
            const allMethods = res.data.data.flatMap((cat: any) => cat.method);
            setPaymentMethods(allMethods || []);
        })
        .catch((err) => console.log("Payment API Error:", err));
    }, [token]);

    useEffect(() => {
        if (defaultPayment) {
            setPayment(defaultPayment);
        }
    }, [defaultPayment]);

    const categories = [
        { label: "Virtual Account", key: "VA" , icon: '/app-icons/transfer.svg'},
        { label: "Dompet Elektronik", key: "EWALLET" , icon: '/app-icons/e-wallet.svg'},
        { label: "QRIS", key: "QRIS" , icon: '/app-icons/qris.svg'},
        { label: "Kartu Kredit", key: "CC" , icon: '/app-icons/credit-card.svg'},
    ];

    const filteredMethods = paymentMethods.filter((pm) => {
        if (!selectedCategory) return false;
        const code = pm.code?.toUpperCase() || "";
        if (selectedCategory === "VA") return ["VA", "I1", "BT", "B1", "M2", "BR"].includes(code);
        if (selectedCategory === "QRIS") return ["SP"].includes(code);
        if (selectedCategory === "EWALLET")
        return ["LA", "SA", "DA", "OV"].includes(code);
        if (selectedCategory === "CC") return code.includes("CC");

        return false;
    })
    .sort((a, b) => {
        if (selectedCategory === "VA") {
        if (a.code === "BR") return -1;
        if (b.code === "BR") return 1;
        }
        return 0;
    });

    const handleSelectPayment = (pm: PaymentMethodData) => {
        const data = {
            ...pm,
            displayName: pm.displayName || pm.name,
            displayIcon: pm.displayIcon || pm.image_url,
        };
        setPayment(data);
        onSelect(data, totalPrice)
    };

    const handleSelectCategory = (catKey: string) => {
        setSelectedCategory(catKey)

        if (catKey === "QRIS") {
            const qrisMethod = paymentMethods.find((pm) =>
                ["SP"].includes(pm.code.toUpperCase())
            );

            if (qrisMethod) {
                handleSelectPayment({
                    ...qrisMethod,
                    displayName: "QRIS",
                    displayIcon: "/app-icons/qris.svg",
                });
            }

            return;
        }

        setStep("detail");
    }

    return (
        <div className="min-h-screen bg-gray-100">
        <HeaderWithBack title="Metode Pembayaran" />

        {step === "menu" && (
            <div className="px-4 mt-4 space-y-3">
            {categories.map((cat) => (
                <div
                    key={cat.key}
                    onClick={() => handleSelectCategory(cat.key)}
                    className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
                >
                <div className="flex items-center gap-3">
                    <img
                        src={cat.icon}
                        alt={cat.label}
                        className="w-8 h-8 object-contain"
                    />
                    <span className="font-medium text-gray-800">
                        {cat.label}
                    </span>
                </div>
                {cat.key === "QRIS" ? (
                    <input
                        type="radio"
                        checked={selectedCategory === "QRIS"}
                        readOnly
                        className="w-5 h-5 accent-teal-500"
                    />
                    ) : (
                    <FaChevronRight className="text-gray-400" />
                )}
                </div>
            ))}
            </div>
        )}

        {step === "detail" && (
            <div className="px-4 mt-4 space-y-3">
            <button
                onClick={() => {
                    setStep("menu");
                    setSelectedCategory(null);
                }}
                className="flex items-center gap-2 text-sm text-gray-600"
            >
                <FaArrowLeft /> Kembali
            </button>

            {filteredMethods.map((pm) => (
                <div
                    key={pm.id}
                    onClick={() => handleSelectPayment(pm)}
                    className={`bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer
                    ${selectedPayment?.id === pm.id ? "border-2 border-teal-500" : ""}
                    `}
                >
                    <div className="flex items-center gap-3">
                        <img
                            src={pm.image_url}
                            alt={pm.name}
                            className="w-10 h-10 object-contain"
                        />
                        <p className="font-medium text-gray-800">{pm.name}</p>
                    </div>
                    <input
                        type="radio"
                        checked={selectedPayment?.id === pm.id}
                        onChange={() => handleSelectPayment(pm)}
                        className="w-5 h-5 accent-teal-500"
                    />
                </div>
            ))}
            </div>
        )}
    </div>
  );
};

export default PaymentMethod;
